import * as openpgp from "openpgp";
import { IEbiAgentGrantRequest } from "dto/Ebi/IEbiOffice";
import { getCompanyPrivateKeys } from "./companyContentKeys";
import { readPublicKey } from "./ebiKeyBinding";
import { getActiveCompanyKey, getCompanyPublicKey } from "./encryptForCompany";
import { readAsBase64 } from "./readAsBase64";

// Grant klucza firmy dla usługi przeszyfrowań EBI (ebi-agent, LegalMail/ebi-agent): PGP(usługa.pub){firma.priv} —
// ten sam kształt co grant członka firmy (GenerateSingleBinary / decryptCompanyPrivateKeys), tylko adresatem jest klucz
// publiczny usługi przysłany przez serwer. Buduje go przeglądarka pracownika, który trzyma klucz prywatny firmy
// w pamięci; serwer klucza firmy nie zna i sprawdza jedynie po pakietach PKESK, że adresatem jest dokładnie klucz usługi.
// Klucz prywatny firmy opuszcza przeglądarkę WYŁĄCZNIE zaszyfrowany kluczem usługi (I4).

export const buildEbiAgentGrant = async (
  agentPublicKeyBase64: string,
): Promise<IEbiAgentGrantRequest> => {
  const { WorkspaceKeyId } = await getActiveCompanyKey();
  const fingerprint = (await getCompanyPublicKey()).getFingerprint();
  const privateKey = getCompanyPrivateKeys().find((k) => k.getFingerprint() === fingerprint);

  if (!privateKey) {
    throw new Error(
      "Klucz firmy usłudze wydaje pracownik z kluczem firmy — ta przeglądarka go nie ma. Odśwież aplikację albo poproś administratora o wydanie klucza.",
    );
  }

  if (!privateKey.isDecrypted()) {
    throw new Error("Klucz firmy w tej przeglądarce jest zablokowany — odśwież aplikację.");
  }

  const agentKey = await readPublicKey(agentPublicKeyBase64);

  if (agentKey.getFingerprint() === fingerprint) {
    throw new Error("Klucz usługi nie może być kluczem firmy.");
  }

  const { message } = await openpgp.encrypt({
    message: openpgp.message.fromBinary(new Uint8Array(privateKey.toPacketlist().write())),
    publicKeys: [agentKey],
    armor: false,
  });

  return {
    WorkspaceKeyId,
    CompanyKeyGrant: await readAsBase64(new Uint8Array(message.packets.write())),
  };
};
